import React from 'react';
import type { PtDirectoryPatient } from '../../../patients-list/types';
import { EmptyState } from '../../EmptyState/EmptyState';
import { PatientDirectoryCard } from './PatientDirectoryCard';

export interface PatientsSearchResultsProps {
  query: string;
  patients: PtDirectoryPatient[];
  minWidth: number;
  showPhotos?: boolean;
  isLoading?: boolean;
  onSelectPatient?: (patient: PtDirectoryPatient) => void;
}

export function PatientsSearchResults({
  query,
  patients,
  minWidth,
  showPhotos = true,
  isLoading = false,
  onSelectPatient,
}: PatientsSearchResultsProps) {
  if (isLoading) {
    return (
      <section className="max-w-[1640px] px-[30px] pb-10">
        <div className="overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-6 py-10 text-center text-[14px] text-[var(--ink-3)]">
          Searching patients…
        </div>
      </section>
    );
  }

  if (!patients.length) {
    return (
      <section className="max-w-[1640px] px-[30px] pb-10">
        <div className="overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)]">
          <EmptyState
            icon="search"
            title="No patients found"
            sub={`No matches for "${query}". Try a name, phone, email, or procedure.`}
          />
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-[1640px] px-[30px] pb-10">
      <div className="mb-4 flex flex-wrap items-center gap-[11px]">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-[var(--ai-3)]">
          Search results
        </span>
        <span className="font-[var(--font-display)] text-xl font-semibold tracking-[-0.025em] text-[var(--ink-0)]">
          Matches for “{query}”
        </span>
        <span className="rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-[9px] py-0.5 font-[var(--font-mono)] text-xs font-semibold text-[var(--ink-2)]">
          {patients.length}
        </span>
      </div>
      <div
        className="pt-grid"
        style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))` }}
      >
        {patients.map((p) => (
          <PatientDirectoryCard
            key={p.id}
            patient={p}
            showPhotos={showPhotos}
            {...(onSelectPatient ? { onSelect: onSelectPatient } : {})}
          />
        ))}
      </div>
    </section>
  );
}
